import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Gauge } from 'lucide-react';
import {
  getCompaniesAISettings,
  updateCompanyAISettings,
  getCompaniesAiQuota,
  AISettingsView,
  AISettingsInput,
  AiQuotaConfigView,
} from '../../../api/AI/settings';
import AiQuotaDialog from './AiQuotaDialog';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Switch } from '../../../components/ui/Switch';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { PageLayout } from '../../../components/ui/PageLayout';
import AppIcon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

interface EditDialogState {
  aiFeaturesEnabled: boolean;
  azureOpenAIEndpoint: string;
  azureOpenAIChatDeployment: string;
  azureOpenAIApiKey: string;
  clearAIKey: boolean;
}

const AISettings: React.FC = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<AISettingsView[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<AISettingsView | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editState, setEditState] = useState<EditDialogState>({
    aiFeaturesEnabled: false,
    azureOpenAIEndpoint: '',
    azureOpenAIChatDeployment: '',
    azureOpenAIApiKey: '',
    clearAIKey: false,
  });
  const [saving, setSaving] = useState(false);
  const [togglingCompanyId, setTogglingCompanyId] = useState<number | null>(null);
  // CO-355 — the per-company daily AI quota, keyed by companyId so the table row and the dialog
  // read the same record. Loaded alongside the settings rather than on demand: the effective cap
  // belongs in the row, not hidden behind a click.
  const [quotas, setQuotas] = useState<Record<number, AiQuotaConfigView>>({});
  const [quotaCompany, setQuotaCompany] = useState<AiQuotaConfigView | null>(null);

  useEffect(() => {
    loadCompanies();
    loadQuotas();
  }, []);

  const loadQuotas = async () => {
    try {
      const result = await getCompaniesAiQuota();
      if (result.status === 200 && result.payload) {
        setQuotas(Object.fromEntries(result.payload.map(q => [q.companyId, q])));
      }
    } catch (error) {
      // The quota column degrades to a dash; the AI settings table itself still works.
      console.error('Failed to load AI quotas:', error);
    }
  };

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const result = await getCompaniesAISettings();
      if (result.status === 200 && result.payload) {
        setCompanies(result.payload);
      }
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAI = async (company: AISettingsView) => {
    setTogglingCompanyId(company.companyId);
    try {
      const input: AISettingsInput = {
        aiFeaturesEnabled: !company.aiFeaturesEnabled,
      };
      const result = await updateCompanyAISettings(company.companyId, input);
      if (result.status === 200 && result.payload) {
        setCompanies(prev =>
          prev.map(c => c.companyId === company.companyId ? result.payload! : c)
        );
      }
    } catch (error) {
      console.error('Failed to toggle AI:', error);
    } finally {
      setTogglingCompanyId(null);
    }
  };

  const handleOpenEditDialog = (company: AISettingsView) => {
    setSelectedCompany(company);
    setEditState({
      aiFeaturesEnabled: company.aiFeaturesEnabled,
      azureOpenAIEndpoint: company.azureOpenAIEndpoint || '',
      azureOpenAIChatDeployment: company.azureOpenAIChatDeployment || '',
      azureOpenAIApiKey: '',
      clearAIKey: false,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCompany(null);
    setEditState({
      aiFeaturesEnabled: false,
      azureOpenAIEndpoint: '',
      azureOpenAIChatDeployment: '',
      azureOpenAIApiKey: '',
      clearAIKey: false,
    });
  };

  const handleSaveSettings = async () => {
    if (!selectedCompany) return;

    setSaving(true);
    try {
      const input: AISettingsInput = {
        aiFeaturesEnabled: editState.aiFeaturesEnabled,
        azureOpenAIEndpoint: editState.azureOpenAIEndpoint || null,
        azureOpenAIChatDeployment: editState.azureOpenAIChatDeployment || null,
        clearAIKey: editState.clearAIKey,
      };

      // Only include API key if provided and not clearing
      if (!editState.clearAIKey && editState.azureOpenAIApiKey) {
        input.azureOpenAIApiKey = editState.azureOpenAIApiKey;
      }

      const result = await updateCompanyAISettings(selectedCompany.companyId, input);
      if (result.status === 200 && result.payload) {
        setCompanies(prev =>
          prev.map(c => c.companyId === selectedCompany.companyId ? result.payload! : c)
        );
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error('Failed to update AI settings:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      title={t('AI Settings')}
      description={t('Manage AI features and Azure OpenAI configuration for each company. Toggle AI features on/off or configure custom Azure OpenAI credentials.')}
      icon={Bot}
      accentColor="slate"
    >
      <div className="w-full overflow-y-auto">
        {loading ? (
          <div className="flex justify-center p-6">
            <Spinner size="lg" />
          </div>
        ) : (
          <Table aria-label="AI settings table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('Company')}</TableHead>
                <TableHead className="text-center">{t('AI Enabled')}</TableHead>
                <TableHead className="text-center">{t('Custom Key')}</TableHead>
                <TableHead>{t('Endpoint')}</TableHead>
                <TableHead>{t('Deployment')}</TableHead>
                <TableHead className="text-center">{t('aiQuota.column')}</TableHead>
                <TableHead className="text-center">{t('Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.companyId}>
                  <TableCell>
                    <span className="font-medium text-foreground">{company.companyName}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Switch
                        checked={company.aiFeaturesEnabled}
                        onCheckedChange={() => handleToggleAI(company)}
                        disabled={togglingCompanyId === company.companyId}
                      />
                      {togglingCompanyId === company.companyId && (
                        <Spinner size="sm" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <StatusBadge variant={company.hasCustomAIKey ? 'success' : 'neutral'} size="sm">
                      {company.hasCustomAIKey ? t('Yes') : t('No')}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <span className={cn('text-sm', company.azureOpenAIEndpoint ? 'text-foreground' : 'text-muted-foreground')}>
                      {company.azureOpenAIEndpoint || t('Not configured')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={cn('text-sm', company.azureOpenAIChatDeployment ? 'text-foreground' : 'text-muted-foreground')}>
                      {company.azureOpenAIChatDeployment || t('Not configured')}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {(() => {
                      const quota = quotas[company.companyId];
                      if (!quota) return <span className="text-sm text-muted-foreground">&mdash;</span>;
                      if (quota.exemptByOwnKey || !quota.effectiveEnabled) {
                        return <span className="text-sm text-muted-foreground">{t('aiQuota.notMetered')}</span>;
                      }
                      /**
                       * CO-357 — the three tier caps are ONE reversible run under RTL.
                       *
                       * 🔴 Rendered as a bare `4 / 3 / 2`, the bidi algorithm resolves the spaced
                       * separators between two European numbers to the paragraph direction (UAX #9
                       * N1: numbers influence adjacent neutrals as R), so in an Arabic page the run
                       * is reordered to `2 / 3 / 4`. An Arabic operator then reads the HR cap as the
                       * Employee cap and vice versa — on a commercial lever they are about to edit.
                       *
                       * Two defences, because either alone leaves a way to be misread:
                       *  1. `direction: ltr` + `unicode-bidi: isolate` locks the visual order of the
                       *     triple and stops it disturbing (or being disturbed by) the RTL cell
                       *     around it. Same treatment already used for the attendance time range.
                       *     Each value additionally sits in its own `<bdi>` so a future non-numeric
                       *     value (e.g. a localized "∞") cannot re-open the reordering.
                       *  2. The tier→value mapping no longer depends on position at all: the
                       *     tooltip and the accessible name pair each label WITH its own number,
                       *     instead of listing three labels in one run and three numbers in
                       *     another and trusting the reader to line them up.
                       */
                      const tierPairs = [
                        [t('aiQuota.tier.hrAdmin'), quota.effectiveHrAdmin],
                        [t('aiQuota.tier.chiefManager'), quota.effectiveChiefManager],
                        [t('aiQuota.tier.employee'), quota.effectiveEmployee],
                      ] as const;
                      // U+2066/U+2069 (LRI/PDI) around each value — the isolate pattern already used
                      // for interpolated values in the ar locale strings. Needed here too because a
                      // title/aria-label is plain text with no element to carry CSS.
                      const quotaLabel = tierPairs
                        .map(([label, value]) => `${label}: ⁦${value}⁩`)
                        .join(' · ');
                      return (
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="sr-only">{quotaLabel}</span>
                          <span
                            aria-hidden="true"
                            className={cn('text-sm tabular-nums', quota.hasOverride ? 'font-medium text-foreground' : 'text-muted-foreground')}
                            style={{ direction: 'ltr', unicodeBidi: 'isolate' }}
                            title={quotaLabel}
                          >
                            <bdi>{quota.effectiveHrAdmin}</bdi> / <bdi>{quota.effectiveChiefManager}</bdi> / <bdi>{quota.effectiveEmployee}</bdi>
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {quota.hasOverride ? t('aiQuota.customBadge') : t('aiQuota.defaultBadge')}
                          </span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('Edit AI Settings')}
                        onClick={() => handleOpenEditDialog(company)}
                        className="text-primary"
                      >
                        <AppIcon name="Pencil" size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('aiQuota.action')}
                        aria-label={t('aiQuota.action')}
                        disabled={!quotas[company.companyId]}
                        onClick={() => setQuotaCompany(quotas[company.companyId] ?? null)}
                        className="text-primary"
                      >
                        <Gauge size={18} aria-hidden="true" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {companies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    {t('No companies found')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) handleCloseEditDialog(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {t('Configure AI Settings')}: {selectedCompany?.companyName}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Switch
              checked={editState.aiFeaturesEnabled}
              onCheckedChange={(checked) => setEditState(prev => ({ ...prev, aiFeaturesEnabled: checked }))}
              label={t('AI Features Enabled')}
            />

            <Input
              label={t('Azure OpenAI Endpoint')}
              value={editState.azureOpenAIEndpoint}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditState(prev => ({ ...prev, azureOpenAIEndpoint: e.target.value }))}
              description={t('The Azure OpenAI resource endpoint URL')}
            />

            <Input
              label={t('Azure OpenAI Chat Deployment')}
              value={editState.azureOpenAIChatDeployment}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditState(prev => ({ ...prev, azureOpenAIChatDeployment: e.target.value }))}
              description={t('The deployment name for the chat model')}
            />

            <Input
              label={t('API Key')}
              type="password"
              value={editState.azureOpenAIApiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditState(prev => ({ ...prev, azureOpenAIApiKey: e.target.value, clearAIKey: false }))}
              disabled={editState.clearAIKey}
              description={t('The API key is write-only and never displayed for security')}
            />

            {selectedCompany?.hasCustomAIKey && (
              <Checkbox
                checked={editState.clearAIKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditState(prev => ({ ...prev, clearAIKey: e.target.checked, azureOpenAIApiKey: '' }))}
                label={t('Clear API Key (remove custom key)')}
              />
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog} disabled={saving}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              loading={saving}
            >
              {saving ? t('Saving...') : t('Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* CO-355 — per-company daily AI quota (Operator only). */}
      <AiQuotaDialog
        open={quotaCompany !== null}
        config={quotaCompany}
        onClose={() => setQuotaCompany(null)}
        onSaved={updated => setQuotas(prev => ({ ...prev, [updated.companyId]: updated }))}
      />
    </PageLayout>
  );
};

export default AISettings;
